import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Printer } from '@phosphor-icons/react';
import { useCars } from '../context/CarsContext';
import CarPrintTemplate from '../components/CarPrintTemplate';
import PrintControls from '../components/print/PrintControls';
import './CarPrintPage.css';

/**
 * Bulk Print Page — рендерит N print-шаблонов друг под другом и вызывает
 * `window.print()` после того как ВСЕ QR-коды и картинки полностью отрисованы.
 * На печати каждая машина выходит на отдельной странице (см.
 * `.print-template-page` page-break правила в CarPrintPage.css), итог —
 * один многостраничный PDF (1 страница на машину).
 *
 * URL: `/cars/print-bulk?ids=1,2,3&token=...&autoprint=true`
 *   - `ids` — comma-separated car IDs из инвентори (требуется)
 *   - `token` — frontend-token (для QR на лендинг)
 *   - `autoprint=true` — авто-вызов window.print() после готовности рендера
 */
const CarPrintBulkPage = () => {
  const { getCarById, loading } = useCars();
  const bulkContainerRef = useRef(null);

  // Controls (issue #1021). Mirror/color/orientation/language применяются
  // глобально ко всем bulk-карточкам — каждая машина рендерится с теми же
  // настройками, не индивидуально.
  const [mode, setMode] = useState('full'); // 'full' | 'qr-only'
  const [format, setFormat] = useState('pdf'); // 'pdf' | 'svg'
  const [orientation, setOrientation] = useState('horizontal');
  const [mirror, setMirror] = useState('off');
  const [color, setColor] = useState('black');
  const [cutMarks, setCutMarks] = useState(false);
  const [language, setLanguage] = useState(() => {
    if (typeof navigator === 'undefined') return 'fr';
    const code = (navigator.language || '').toLowerCase().split('-')[0];
    return ['fr', 'en', 'de', 'lu'].includes(code) ? code : 'fr';
  });
  const [downloading, setDownloading] = useState(false);

  // Достаём ids из URL hash (HashRouter): /#/cars/print-bulk?ids=1,2,3
  const ids = useMemo(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const idsRaw = params.get('ids') || '';
    return idsRaw.split(',').map((s) => s.trim()).filter(Boolean);
  }, []);

  const cars = useMemo(() => {
    if (loading) return [];
    return ids
      .map((id) => getCarById(id))
      .filter((c) => Boolean(c));
  }, [ids, getCarById, loading]);

  // Гард на «вообще не нашли ни одной машины из переданных ids».
  const noCarsFound = !loading && ids.length > 0 && cars.length === 0;

  // used-VIN flow (Andys и пр.) — НЕ new-model (Autodis). Для него листовка
  // landscape-only: прячем Orientation, форсим horizontal (кроме qr-only —
  // он остаётся вертикальным sticker'ом), не инвертируем по умолчанию и
  // даём панели нейтральный хром. Autodis (new-model) этим не затрагивается. (#1064)
  const isUsedVin = cars.length > 0 && cars.every((c) => !c?.isNewModel);
  const effectiveOrientation =
    isUsedVin && mode !== 'qr-only' ? 'horizontal' : orientation;
  // used-VIN (Andys) — мультиязычности нет, форсим English ('Powered by'
  // вместо 'Propulsé par'). Autodis (new-model) сохраняет язык. (#1064)
  const effectiveLanguage = isUsedVin ? 'en' : language;

  // used-VIN: дефолт Color = 'white' (не инвертировано). Срабатывает один раз
  // когда определили used-VIN; оператор потом может выбрать инверсию вручную.
  useEffect(() => {
    if (isUsedVin) setColor('white');
  }, [isUsedVin]);

  // Готовность QR-кодов (по одному callback на каждую CarPrintTemplate).
  // qr-code-styling резолвит append() асинхронно — без этого окно печати
  // открывалось до того как QR-коды появились на странице.
  const [qrReadyCount, setQrReadyCount] = useState(0);
  const handleQrReady = useCallback(() => {
    setQrReadyCount((n) => n + 1);
  }, []);

  // Готовность картинок. expectedImages считаем динамически — сканируем DOM
  // после рендера CarPrintTemplate. Так не привязываемся к знанию что
  // CarPrintTemplate рендерит ровно 2 <img> per car (если дизайн добавит/уберёт
  // asset, счётчик подстроится автоматически).
  // `imagesCounted` — отдельный флаг чтобы отличать «ещё не считали» от
  // «честный 0 <img>» (template без <img>-ов после inline SVG-фикса);
  // без флага allReady застревал на `expectedImages > 0`.
  const [expectedImages, setExpectedImages] = useState(0);
  const [imagesCounted, setImagesCounted] = useState(false);
  const [imagesLoadedCount, setImagesLoadedCount] = useState(0);

  // После того как cars отрисованы, считаем реальное число <img> внутри
  // .print-template-page. Двойной requestAnimationFrame гарантирует что
  // браузер прошёл layout/paint после React commit-а.
  useEffect(() => {
    if (cars.length === 0) {
      setExpectedImages(0);
      setImagesCounted(false);
      return undefined;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const count = document.querySelectorAll('.print-template-page img').length;
        setExpectedImages(count);
        setImagesCounted(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [cars.length]);

  // Готовы когда ВСЕ счётчики дотянули до своих ожиданий.
  const allReady = cars.length > 0
    && qrReadyCount >= cars.length
    && imagesCounted
    && imagesLoadedCount >= expectedImages;

  // Сбрасываем счётчики при смене набора cars (HMR/dev/повторный рендер).
  useEffect(() => {
    setQrReadyCount(0);
    setImagesLoadedCount(0);
    setImagesCounted(false);
  }, [cars.length]);

  // Auto-print / watchdog убраны (issue #949): user сам жмёт Print или
  // Download, никакого автоматического window.print() при загрузке.

  // Override default @page для landscape | portrait в зависимости от
  // orientation state (см. подробное обоснование в CarPrintPage.jsx).
  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.printOrientation = 'qrtemplate-bulk';
    const pageSize = effectiveOrientation === 'vertical' ? 'A4 portrait' : 'A4 landscape';
    style.textContent = `@page { size: ${pageSize}; margin: 0; }`;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [effectiveOrientation]);

  // QR-only mode — только portrait (см. CarPrintPage.jsx, та же логика).
  // Безусловный capture ref'а при входе в qr-only — иначе full+vertical →
  // qr-only → full сбрасывает выбор юзера на initial 'horizontal'.
  const priorOrientationRef = useRef(orientation);
  useEffect(() => {
    if (mode === 'qr-only') {
      priorOrientationRef.current = orientation;
      if (orientation !== 'vertical') {
        setOrientation('vertical');
      }
    } else if (priorOrientationRef.current !== orientation) {
      setOrientation(priorOrientationRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleBulkDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { buildSvg, downloadSvg, downloadPdf, buildFilename } = await import(
        '../components/print/exporters/templateExporter'
      );
      const pages = bulkContainerRef.current?.querySelectorAll('.print-template-page .print-template') || [];
      // Defensive: React render гарантирует pages.length === cars.length
      // (по одной .print-template-page на каждый car), но если future
      // рефакторинг сломает контракт — лог поможет диагностировать вместо
      // тихих "Failed for #5" в alert.
      if (pages.length !== cars.length) {
        console.warn(
          '[CarPrintBulkPage] DOM/data length mismatch:',
          pages.length, 'pages vs', cars.length, 'cars',
        );
      }
      // Каждая машина — отдельный файл (зипать сложнее, юзер сам сложит).
      // Если в будущем понадобится один многостраничный PDF — собирать через
      // pdf.addPage() и serialize всех svg.
      //
      // Per-car try/catch (#949 review): одна сломанная машина не должна
      // отменять остальные. Соберём failures и покажем итоговый summary,
      // юзер ретраит только проблемные car-ids.
      const failures = [];
      for (let i = 0; i < pages.length; i += 1) {
        const car = cars[i];
        try {
          const svgText = await buildSvg(pages[i], car, mode);
          const filename = buildFilename(car, mode, format);
          if (format === 'svg') {
            downloadSvg(svgText, filename);
          } else {
            await downloadPdf(svgText, filename);
          }
        } catch (err) {
          console.error('[CarPrintBulkPage] car', car?.id, 'export failed:', err);
          failures.push(car?.id || `#${i + 1}`);
        }
      }
      if (failures.length) {
        alert(`Failed for ${failures.length}/${pages.length} cars: ${failures.join(', ')}`);
      }
    } catch (err) {
      console.error('[CarPrintBulkPage] bulk export failed before per-car loop:', err);
      alert(`Failed to generate ${format.toUpperCase()}: ${err?.message || err}`);
    } finally {
      setDownloading(false);
    }
  }, [cars, downloading, format, mode]);

  if (loading) {
    return <div className="print-template__loading">Loading...</div>;
  }

  if (noCarsFound) {
    return (
      <div className="print-template__loading">
        No cars found for IDs: {ids.join(', ')}
      </div>
    );
  }

  const actionLabel = !allReady
    ? 'Preparing…'
    : downloading
      ? 'Generating…'
      : `Download ${cars.length} as ${format.toUpperCase()}`;

  const bulkRenderClasses = ['print-template-render'];
  if (mirror === 'on') bulkRenderClasses.push('print-template-render--mirror');
  if (color === 'black') bulkRenderClasses.push('print-template-render--inverted');
  if (cutMarks && mode === 'qr-only') bulkRenderClasses.push('print-template-render--cut-marks');
  const bulkRenderClass = bulkRenderClasses.join(' ');

  // showLanguage = все cars в bulk-выборке new-model. Mixed bulk скрывает
  // toggle — used-VIN i18n integration пока не сделана.
  const showLanguage = cars.length > 0 && cars.every((c) => c?.isNewModel);

  return (
    <div className={`print-template-wrapper print-template-wrapper--bulk${isUsedVin ? ' print-template-wrapper--neutral-chrome' : ''}`}>
      <div className={bulkRenderClass} ref={bulkContainerRef}>
      {cars.map((car) => (
        // page-break-after: always через `.print-template-page` —
        // каждая машина уходит на отдельную страницу при печати.
        <div key={car.id} className="print-template-page">
          <CarPrintTemplate car={car} mode={mode} orientation={effectiveOrientation} language={effectiveLanguage} onQrReady={handleQrReady} />
        </div>
      ))}
      </div>

      <PrintControls
        language={language}
        onLanguageChange={setLanguage}
        showLanguage={showLanguage}
        mode={mode}
        onModeChange={setMode}
        showMode={!isUsedVin}
        orientation={effectiveOrientation}
        onOrientationChange={setOrientation}
        showOrientation={!isUsedVin}
        showCatalogQr={!isUsedVin}
        showMirror={!isUsedVin}
        mirror={mirror}
        onMirrorChange={setMirror}
        showColor={!isUsedVin}
        color={color}
        onColorChange={setColor}
        format={format}
        onFormatChange={setFormat}
        cutMarks={cutMarks}
        onCutMarksChange={setCutMarks}
      >
        <button
          type="button"
          className="print-template__action-btn print-template__action-btn--compact"
          onClick={handleBulkDownload}
          disabled={!allReady || downloading}
        >
          <Download className="print-template__action-btn-icon" weight="regular" />
          {actionLabel}
        </button>
        <button
          type="button"
          className="print-template__action-btn print-template__action-btn--compact"
          onClick={() => window.print()}
          disabled={!allReady || downloading}
        >
          <Printer className="print-template__action-btn-icon" weight="regular" />
          {allReady ? `Print ${cars.length}` : 'Preparing…'}
        </button>
      </PrintControls>

      <ImageLoadCounter expected={expectedImages} onChange={setImagesLoadedCount} />

      {/* Loading overlay — показываем пока готовится bulk-рендер.
          Скрываем на печати через @media print, чтобы overlay не попал
          в готовый PDF. */}
      {!allReady && (
        <div className="print-template__preparing" role="status" aria-live="polite">
          <div className="print-template__preparing-spinner" aria-hidden="true" />
          <div className="print-template__preparing-text">
            Preparing {cars.length} {cars.length === 1 ? 'template' : 'templates'}…
          </div>
          <div className="print-template__preparing-detail">
            QR codes: {qrReadyCount}/{cars.length} · Images: {imagesLoadedCount}/{expectedImages}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Считает сколько <img> в текущем документе закончили грузиться (событиями
 * `load` и `error`, чтобы не зависнуть на сломанной картинке). MutationObserver
 * ловит элементы добавленные после маунта (React-render внутри `.print-template-page`).
 */
const ImageLoadCounter = ({ expected, onChange }) => {
  useEffect(() => {
    if (expected === 0) {
      onChange(0);
      return undefined;
    }

    let loaded = 0;
    let cancelled = false;
    const seen = new WeakSet();

    const handleResolve = () => {
      if (cancelled) return;
      loaded += 1;
      onChange(loaded);
    };

    const trackImage = (img) => {
      if (seen.has(img)) return;
      seen.add(img);
      if (img.complete && img.naturalWidth > 0) {
        handleResolve();
        return;
      }
      const onceLoad = () => {
        img.removeEventListener('load', onceLoad);
        // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
        img.removeEventListener('error', onceError);
        handleResolve();
      };
      const onceError = () => {
        img.removeEventListener('load', onceLoad);
        img.removeEventListener('error', onceError);
        handleResolve();
      };
      img.addEventListener('load', onceLoad);
      img.addEventListener('error', onceError);
    };

    document.querySelectorAll('.print-template-page img').forEach(trackImage);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') {
            trackImage(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('img').forEach(trackImage);
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [expected, onChange]);

  return null;
};

export default CarPrintBulkPage;
